/**
 * Single source of truth for plan / price data on the frontend.
 *
 * Price IDs come from per-environment Vite env (.env.qa / .env.production) with
 * fallbacks to the current Stripe TEST-mode IDs so dev keeps working when unset.
 * Backend mirror: cdk/lib/config/environment-config.ts (passed to the lambdas as
 * env vars). When swapping test → live prices at launch, edit those two config
 * sources — never the application code. See xdesign_docs/billing.md §1, §8, §9.
 */

const env = import.meta.env;

const PRICE_MONTHLY = env.VITE_PRICE_MONTHLY || 'price_1TcJ75DsfgHjUvqKUrRH5Zuw';
const PRICE_ANNUAL = env.VITE_PRICE_ANNUAL || 'price_1TcJ75DsfgHjUvqKCyB6Nmcb';
const PRICE_ELITE = env.VITE_PRICE_ELITE || 'price_1TcI4rDsfgHjUvqKm8wuVq0d';

// Legacy price IDs — older subscriptions still in the wild. Display + rank only;
// these are never offered for new purchases. Rank matches the current equivalent
// so a legacy-annual subscriber switching to monthly is a deferred downgrade.
const LEGACY = {
  'price_1TbueODsfgHjUvqKivp2IwOx': { name: 'PrimeMaxing Monthly', rank: 1 },
  'price_1TcHt3DsfgHjUvqKx42RBTFX': { name: 'PrimeMaxing Annual', rank: 2 },
};

// Canonical catalog, ordered by rank (the upgrade direction: Elite > Annual > Monthly).
// rank is a business ordering, NOT a per-year amount — see billing.md §1.
export const PLAN_CATALOG = [
  { key: 'monthly', priceId: PRICE_MONTHLY, label: 'PrimeMaxing Monthly', amount: '$20', interval: '/month', rank: 1 },
  { key: 'annual', priceId: PRICE_ANNUAL, label: 'PrimeMaxing Annual', amount: '$199', interval: '/year', rank: 2 },
  { key: 'elite', priceId: PRICE_ELITE, label: 'PrimeMaxing Elite', amount: '$10,000', interval: '/year', rank: 3 },
];

export const PLAN_NAMES = {
  ...Object.fromEntries(PLAN_CATALOG.map(p => [p.priceId, p.label])),
  ...Object.fromEntries(Object.entries(LEGACY).map(([id, v]) => [id, v.name])),
};

export const PLAN_RANKS = {
  ...Object.fromEntries(PLAN_CATALOG.map(p => [p.priceId, p.rank])),
  ...Object.fromEntries(Object.entries(LEGACY).map(([id, v]) => [id, v.rank])),
};

// Plans offered as in-app switch targets. Elite is included (upgrade only — the
// current plan is filtered out at the call site, so Elite never appears for an
// Elite member). See billing.md §4.
export const SWITCHABLE_PLANS = PLAN_CATALOG.map(
  ({ priceId, label, amount, interval, rank }) => ({ priceId, label, amount, interval, rank })
);

// Free-trial length (days) for display copy only. The server (TRIAL_DAYS env on the
// checkout lambda) is authoritative for the actual trial length. Mirrors the VITE_PRICE_*
// dual-config pattern above. Trial attaches to the Monthly plan.
export const TRIAL_DAYS = parseInt(env.VITE_TRIAL_DAYS, 10) || 7;

// The plan a trial converts into — Monthly. Used by the trial-activation CTA.
export const TRIAL_PLAN = PLAN_CATALOG.find(p => p.key === 'monthly');
